import { deepCloneJSON } from "@/lib/duplicate-object";
import { DEFAULT_USER_DISPLAY_PREFERENCES } from "@/user-preferences/default-user-preferences";
import type { UserPreferences } from "@/user-preferences/types";
import { migrateUserPreferences } from "@/user-preferences/user-display-preferences-migration";

/**
 * Ensures `display`, `pages.pagePreferences`, `accessibility`, and `downloads` exist
 * by merging with defaults. Sets `wasRepaired` when any required branch was missing or invalid.
 */
export function normalizeUserPreferences(input: UserPreferences): {
    preferences: UserPreferences;
    wasRepaired: boolean;
} {
    const defaults = deepCloneJSON(DEFAULT_USER_DISPLAY_PREFERENCES);
    let wasRepaired = false;

    if (!input?.pages || typeof input.pages !== "object") {
        wasRepaired = true;
    } else if (!input.pages.pagePreferences || typeof input.pages.pagePreferences !== "object") {
        wasRepaired = true;
    } else if (!input.display || typeof input.display !== "object") {
        wasRepaired = true;
    } else if (!input.accessibility || typeof input.accessibility !== "object") {
        wasRepaired = true;
    } else if (!input.downloads || typeof input.downloads !== "object") {
        wasRepaired = true;
    }

    const pagesPartial =
        input.pages && typeof input.pages === "object" ? input.pages : ({} as Partial<UserPreferences["pages"]>);
    const ppPartial =
        pagesPartial.pagePreferences && typeof pagesPartial.pagePreferences === "object"
            ? pagesPartial.pagePreferences
            : {};

    const preferences: UserPreferences = {
        ...defaults,
        ...input,
        display: {
            ...defaults.display,
            ...(input.display && typeof input.display === "object" ? input.display : {}),
        } as UserPreferences["display"],
        pages: {
            ...defaults.pages,
            ...pagesPartial,
            pagePreferences: {
                ...defaults.pages.pagePreferences,
                ...ppPartial,
            } as UserPreferences["pages"]["pagePreferences"],
        },
        accessibility: {
            ...defaults.accessibility,
            ...(input.accessibility && typeof input.accessibility === "object" ? input.accessibility : {}),
        } as UserPreferences["accessibility"],
        downloads: {
            ...defaults.downloads,
            ...(input.downloads && typeof input.downloads === "object" ? input.downloads : {}),
        } as UserPreferences["downloads"],
        version: typeof input.version === "number" ? input.version : defaults.version,
        lastUpdated: typeof input.lastUpdated === "string" ? input.lastUpdated : defaults.lastUpdated,
    };

    return { preferences, wasRepaired };
}

function migrateUserPreferencesSafe(raw: unknown): { preferences: UserPreferences; failed: boolean } {
    try {
        if (!raw || typeof raw !== "object") {
            return { preferences: deepCloneJSON(DEFAULT_USER_DISPLAY_PREFERENCES), failed: false };
        }
        return { preferences: migrateUserPreferences(raw as UserPreferences), failed: false };
    } catch (e) {
        console.error("[preferences] migration failed", e);
        return { preferences: deepCloneJSON(DEFAULT_USER_DISPLAY_PREFERENCES), failed: true };
    }
}

/**
 * Load preferences from the server payload: migrate, normalize shape, and report if data looked corrupt.
 */
export function loadAndNormalizeServerPreferences(server: UserPreferences | null | undefined): {
    preferences: UserPreferences;
    showCorruptionWarning: boolean;
} {
    if (!server) {
        return { preferences: deepCloneJSON(DEFAULT_USER_DISPLAY_PREFERENCES), showCorruptionWarning: false };
    }

    const { preferences: migrated, failed: migrateFailed } = migrateUserPreferencesSafe(server);
    const { preferences, wasRepaired } = normalizeUserPreferences(migrated);
    return {
        preferences,
        showCorruptionWarning: migrateFailed || wasRepaired,
    };
}
